import { FC } from 'react'
import styles from 'components/features/socialFeed/FeedPost/FeedPostMeta.styl'
import { PostShareButton } from 'components/features/pfm/SegmentButton/PostShareButton'
import { PostFollowButton } from 'components/features/pfm/SegmentButton/PostFollowButton'
import { PostLikeButton } from 'components/features/pfm/SegmentButton/PostLikeButton'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { togglePostFollowingActionCreator, togglePostLikeActionCreator } from 'actions/feedActions'
import { PostUid } from 'interfaces/feed'
import { useCallback, useMemo } from 'react'
import { feedPostSelector, createPostAuthorSelector, isOwnPostSelector, feedAppSelector } from 'selectors/feedSelectors'
import { ShareableInfo } from 'interfaces/share'
import { registerShareActionCreator } from 'actions/socialActions'
import { FeedLinkLoggedOut } from 'components/features/socialFeed/FeedLinkLoggedOut/FeedLinkLoggedOut'
import { compareViewports } from 'helpers/viewportHelpers'
import { currentViewportSelector } from 'selectors/viewportSelectors'
import { useMatchMedia } from 'helpers/hookHelpers'
import { isSocialInsightPost } from 'helpers/feedHelpers'

const HIDE_ICONS_BREAKPOINTS = [768, 1024] // px
const HIDE_ICONS_DELTA = 30 // px

const HIDE_ICONS_DESKTOP_QUERY = HIDE_ICONS_BREAKPOINTS
  .map(
    (point) => `(min-width: ${ point }px) and (max-width: ${ point + HIDE_ICONS_DELTA }px)`
  )
  .join(', ')

type FeedPostMetaProps = {
  postUid: PostUid
  allowShare?: boolean
  allowFollow?: boolean
}

export const FeedPostMeta: FC<FeedPostMetaProps> = (props) => {
  const {
    postUid,
    allowShare = true,
    allowFollow = true,
  } = props

  const postUidObj = useMemo(
    () => ({ postUid }),
    [postUid]
  )
  const post = useStoreSelector((state) => feedPostSelector(state, postUidObj))
  const postAuthorSelector = useMemo(
    createPostAuthorSelector,
    [postUid]
  )
  const postAuthor = useStoreSelector((state) => postAuthorSelector(state, postUidObj))

  const dispatch = useStoreDispatch()

  const isOwnPost = useStoreSelector((state) => isOwnPostSelector(state, postUidObj))

  const feedLoadingStates = useStoreSelector(feedAppSelector)
  const isLikeLoading = Boolean(feedLoadingStates.likingPostUids[postUid])
  const isPostFollowLoading = Boolean(feedLoadingStates.postFollowingLoadingIds[postUid])

  const handleShareClick = useCallback((info: ShareableInfo) => {
    dispatch(registerShareActionCreator(info))
  }, [dispatch])

  const postIsFollowed = post && !isSocialInsightPost(post) ? post.is_followed : null
  const handleFollowClick = useCallback(() => {
    if (isPostFollowLoading || !post || isSocialInsightPost(post)) {
      return
    }

    dispatch(togglePostFollowingActionCreator.request({ postUid, toFollow: !postIsFollowed }))
  }, [dispatch, postUid, post, postIsFollowed, isPostFollowLoading])

  const postIsLiked = post ? post.is_liked : null
  const postAuthorUid = postAuthor ? postAuthor.uid : ''
  const handleLikeClick = useCallback(() => {
    if (isLikeLoading || !post || !postAuthor) {
      return
    }

    dispatch(togglePostLikeActionCreator.request({ postUid, toLike: !postIsLiked, authorUid: postAuthorUid }))
  }, [dispatch, post, postAuthor, postUid, postIsLiked, isLikeLoading, postAuthorUid])


  const hideIconsDesktop = useMatchMedia(HIDE_ICONS_DESKTOP_QUERY)
  const hideIconsMobile = compareViewports(
    useStoreSelector(currentViewportSelector),
    '<',
    'w375'
  )

  const hideButtonIcon = hideIconsDesktop || hideIconsMobile

  if (!postAuthor || !post || isSocialInsightPost(post)) {
    return null
  }

  const { text: postText, followers_count, likes_count, is_followed, is_liked } = post
  const { username: authorName } = postAuthor

  const shareTitle = `${ authorName } on StatusMoney`
  const shareText = postText.length > 100
    ? postText.slice(0, 100) + '…'
    : postText

  return (
    <div className={ styles.container }>
      <div className={ styles.content }>
        <div className={ styles.left }>
          { allowShare && (
            <div className={ styles.buttonWrapper }>
              <PostShareButton
                title={ shareTitle }
                content={ shareText }
                url={ post.short_url }
                hideIcon={ hideButtonIcon }
                onShare={ handleShareClick }
              />
            </div>
          )}
        </div>
        <div className={ styles.right }>
          <div className={ styles.buttonWrapper }>
            { allowFollow && !isOwnPost && (
              <FeedLinkLoggedOut type='post' postUid={ postUid }>
                {
                  (isAuthenticated) => (
                    <PostFollowButton
                      counter={ followers_count }
                      pressed={ is_followed }
                      hideIcon={ hideButtonIcon }
                      onClick={ isAuthenticated ? handleFollowClick : undefined }
                    />
                  )
                }
              </FeedLinkLoggedOut>
            )}
          </div>
          <div className={ styles.buttonWrapper }>
            <FeedLinkLoggedOut type='post' postUid={ postUid }>
              {
                (isAuthenticated) => (
                  <PostLikeButton
                    counter={ likes_count }
                    pressed={ is_liked }
                    hideIcon={ hideButtonIcon }
                    onClick={ isAuthenticated ? handleLikeClick : undefined }
                  />
                )
              }
            </FeedLinkLoggedOut>
          </div>
        </div>
      </div>
    </div>
  )
}

